import { useEffect, useMemo, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { fetchApplicationStatusAndRedirectIfNotExpectedState } from '../applicationStatusHelpers';
import { ApplicationStatusModel } from '../../serverGeneratedTypings/applicationStatusModel';
import { CddAnswer } from '../../serverGeneratedTypings/cddAnswer';
import { N2MajorApplicationStatus } from '../../serverGeneratedTypings/n2MajorApplicationStatus';
import CddService from '../../services/cddservice';
import { default as CddForm, CddQuestionGroups, CddSteps, PreFilledAnswer } from '../../components/CddWizard';
import { isPepFieldName, getExtendedQuestions, getAmlQuestions, getPepQuestions } from './cddSpec';
import { logErrorToServerside, logInfoToServerSide } from '../../services/serverSideLogging';
import { pollForExpectedApplicationState, setPpi } from '../../services/legacyApplicationApiService';
import { useApplicationIdContext } from '../../components/ApplicationContext';
import { N2ApplicationStatus } from '../../serverGeneratedTypings/n2ApplicationStatus';
import { navigateToStep, ApplicationPaths } from '../ApplicationFormsHelpers';
import { SubmitState } from '@n2-common/components/MultiStageOnePageWizard/types';
import { CommonStatusPageProps } from '@n2-common/components/ApplicationStatusPage';
import { useTranslation } from 'react-i18next';
import { Question } from '@n2-common/serverGeneratedTypings/question';
import { AnswerOption } from '@n2-common/serverGeneratedTypings/answerOption';
import { useDismissableErrorMessage } from '@n2-common/ui';
import { errorToAction } from '../applicationErrorHelper';

export function applicationIsInValidStateForCdd(applicationStatus: ApplicationStatusModel) {
    if (applicationStatus.MajorStatus !== N2MajorApplicationStatus.Aproved) {
        return false;
    }
    if (applicationStatus.IsMainApplicant) {
        return applicationStatus.ApplicantCddPending || applicationStatus.Status == N2ApplicationStatus.Offer;
    } else {
        if (applicationStatus.Status !== N2ApplicationStatus.ReadyForSigning) {
            return false;
        }
        return applicationStatus.CosignerCddPending;
    }
    return true;
}
type CddCountry = Country;
type CddProduct = 'loan' | 'creditcard' | 'deposit';
type CddProps = {
    product: CddProduct;
    afterCddFunc: (applicationStatus: ApplicationStatusModel, applicantId: string, navigate: NavigateFunction) => Promise<void> | void;
};

type CddCombinedProps = CddProps & CommonStatusPageProps;

function CddPage({ navigateToCorrectStepFunc, product, afterCddFunc }: CddCombinedProps): JSX.Element {
    const navigate = useNavigate();
    const { applicationId, applicantId, country } = useApplicationIdContext();
    const { setErrorMessage, ErrorOutput } = useDismissableErrorMessage();
    const [applicationStatus, setApplicationStatus] = useState<ApplicationStatusModel | null>(null);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [countries, setCountries] = useState<AnswerOption[]>([]);
    const [isMainApplicant, setIsMainApplicant] = useState<boolean | null>(null);

    const { t } = useTranslation('common');

    useEffect(() => {
        const dofetch = async () => {
            const fetched = await CddService.fetchCountries();
            // console.log(fetched);
            setCountries(fetched);
        };

        dofetch();
    }, []);

    useEffect(() => {
        fetchApplicationStatusAndRedirectIfNotExpectedState(
            applicationId,
            applicantId,
            applicationIsInValidStateForCdd,
            setApplicationStatus,
            navigate,
            navigateToCorrectStepFunc
        );
    }, [applicationId, navigate, applicantId, navigateToCorrectStepFunc]);

    useEffect(() => {
        if (!applicationStatus) {
            return;
        }
        const mainApp = applicationStatus.IsMainApplicant;
        logInfoToServerSide(`Showing CDD form for ${mainApp ? 'mainApplicant' : 'coApplicant'}`, applicationId, applicantId);
        setIsMainApplicant(mainApp);
    }, [applicationStatus, applicantId, applicationId]);

    useEffect(() => {
        document.title = t('Cdd.PageTitle');
    }, [t]);

    useEffect(() => {
        const dofetch = async () => {
            const fetched = await CddService.fetchQuestions(productStringFromProduct(product));
            setQuestions(fetched);
        };
        dofetch();
    }, [country, product]);

    const preFilledAnswers: PreFilledAnswer[] | null = useMemo(
        () => mapPrefilledAnswers(applicationStatus, questions, isMainApplicant),
        [applicationStatus, questions, isMainApplicant]
    );

    const questionGroups: CddQuestionGroups = useMemo(
        () => ({
            amlPage1: getAmlQuestions(questions, isMainApplicant),
            pepQuestions: getPepQuestions(questions),
            extendedQuestions: getExtendedQuestions(questions),
        }),
        [isMainApplicant, questions]
    );

    const onSubmit = async (
        answerPayload: CddAnswer[],
        setSubmitState: (state: SubmitState) => void,
        setWizardStep: (step: CddSteps) => void,
        includesEnhanced: boolean
    ) => {
        setSubmitState('Submitted');
        try {
            const res = await CddService.postToKompisApi(applicationId, applicantId, answerPayload, includesEnhanced);

            if (res.ShowEnhancedQuestions) {
                logInfoToServerSide('Asking for enhanced cdd', applicationId, applicantId);
                setSubmitState('NotSent');
                setWizardStep(CddSteps.Extended);
                return;
            }

            setSubmitState('Processing');
            const appStatus = await pollForExpectedApplicationState(applicationId, applicantId, (res: ApplicationStatusModel) =>
                isMainApplicant ? !res.ApplicantCddPending : !res.CosignerCddPending
            );
            await afterCddFunc(appStatus, applicantId, navigate);
        } catch (ex) {
            const action = errorToAction(
                ex,
                () => onSubmit(answerPayload, setSubmitState, setWizardStep, includesEnhanced),
                applicationId,
                applicantId
            );
            if (action) {
                setErrorMessage(action);
                setSubmitState('Error');
                return;
            }
            setSubmitState('Error');
            logErrorToServerside('Error posting CDD answers', applicationId, applicantId, ex);
            navigateToStep(ApplicationPaths.error, applicationId, navigate, applicantId);
            return;
        }
    };

    return (
        <>
            {applicationStatus ? (
                <CddForm
                    onSubmit={onSubmit}
                    isPep={applicationStatus.IsPepTrapets}
                    isPepFieldName={isPepFieldName}
                    countries={countries}
                    questionGrups={questionGroups}
                    preFilledAnswers={preFilledAnswers}
                    questions={questions}
                ></CddForm>
            ) : (
                <>
                    {' '}
                    <span>{t<string>('Loading')}</span>
                </>
            )}
            <ErrorOutput />
        </>
    );
}

export const afterCddFuncForPpiProducts = async (applicationStatus: ApplicationStatusModel, applicantId: string, navigate: NavigateFunction) => {
    const applicationId = applicationStatus.ApplicationId;
    if (applicationStatus.EligiblePPI && applicationStatus.IsMainApplicant && !applicationStatus.OptOutPpiApplied) {
        logInfoToServerSide('Moving to PPI', applicationStatus.ApplicationId, applicantId);
        navigateToStep(ApplicationPaths.insurance, applicationId, navigate, applicantId);
    } else {
        if (applicationStatus.IsMainApplicant) {
            logInfoToServerSide('Skipping PPI-step for main-applicant setting it to null', applicationId, applicantId);
            await setPpi(null, applicationId, applicantId);
            await pollForExpectedApplicationState(
                applicationId,
                applicantId,
                (res: ApplicationStatusModel) => res.Status === N2ApplicationStatus.ReadyForSigning
            );
        } else {
            logInfoToServerSide('Skipping PPI-step for co-applicant', applicationId, applicantId);
        }
        navigateToStep(ApplicationPaths.sign, applicationId, navigate, applicantId);
    }
};

export function mapPrefilledAnswers(
    applicationStatus: Pick<ApplicationStatusModel, 'YearlySalaryForCddForm' | 'EmploymentStatusCode'> | null,
    questions: Question[],
    isMainApplicant: boolean | null
) {
    const arr: PreFilledAnswer[] = [];
    if (!applicationStatus || questions.length === 0 || isMainApplicant === null) {
        return null;
    }
    if (applicationStatus.EmploymentStatusCode && applicationStatus.EmploymentStatusCode !== '' ){
        arr.push({ questionCode: 'EmploymentStatus', answerCode: applicationStatus.EmploymentStatusCode });
    }
   
    const incomeQuestion = questions.find((m) => m.QuestionCode === 'IncomeTo');
    const incomeToUse = applicationStatus.YearlySalaryForCddForm;
    if (incomeQuestion && applicationStatus && incomeToUse && incomeToUse !== null) {
        const correctAnswer = getIncomeAnswer(incomeQuestion, incomeToUse);
        arr.push({ questionCode: 'IncomeTo', answerCode: correctAnswer.AnswerCode });
    }

    return arr;
}

export function getIncomeAnswer(incomeQuestion: Question, incomeToUse: number) {
    const inRange = incomeQuestion.AnswerOptions.filter((m) => parseInt(m.AnswerCode, 10) >= (incomeToUse || 0));
    inRange.sort((a, b) => (parseInt(a.AnswerCode, 10) > parseInt(b.AnswerCode, 10) ? 1 : -1));
    const correctAnswer = inRange[0];
    return correctAnswer;
}

export default CddPage;

export function cultureFromCountry(country: CddCountry): string {
    switch (country) {
        case 'SE':
            return 'sv-SE';
        case 'FI':
            return 'fi-FI';
        case 'NO':
            return 'nb-NO';
    }
}

function productStringFromProduct(product: CddProduct): string {
    switch (product) {
        case 'loan':
            return 'loanProduct=true';
        case 'creditcard':
            return '&cardProduct=true';
        case 'deposit':
            return '&depositProduct=true';
    }
}
